import { Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import svgSpriteSheet from "@texthelp/styleguide-global-tasks/dist/all.svg?url";
import { getTheme } from "../functions/themeService/themeService";
import { createSpriteSheet } from "../spriteSheetUtils";
import NavComponent from "../components/nav/NavComponent";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import LoadingScreen from "../components/loadingScreen/LoadingScreen";
import SettingsModal from "../components/settingsModal/SettingsModalComponent";
import MobileNav from "../components/mobileNav/MobileNav";
import { SlideoutMenu, SlideoutMenuButton } from "../components/slideoutMenu/SlideoutMenu";
import EditModal from "../components/editModal/EditModal";
import PageControl from "../components/pageControl/PageControl";
import { setUserDetails } from "../redux/actions/setUserDetailsAction";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext } from "react-beautiful-dnd";
import { onDragStartFunction, onDragEndFunction } from "../functions/dragFunctions/dragFunctions";
import "../App.css";
import { findBy$IdInTreeList } from "../utils/tree";
import UnsortedTasksPanel from "../components/unsortedPanel/UnsortedPanel";
import NotificationButtonModal from "../components/notificationModal/NotificationButtonModal"
import signOut from "../functions/userIdentity/signOut";
import { setLicense } from "../functions/userIdentity/setLicense";
import { useIsIOS } from "../functions/deviceIdentity/getDeviceType";
import { useIsMobile } from "../hooks/useIsMobile";
import LicensingModal from "../components/licenseModal/LicenseScreenModal";
import { setUserSettings } from '../redux/actions/Settings/setSettingslAction';
import { askForNotificationPermission, getSubscription } from "../functions/notifications/notificationFunctions";
import { getFirebaseLicenseFile } from "../redux/actions/Firebase/getFirebaseLicenseAction";
import { sendEvent, setMobileProperties } from "../redux/actions/analyticsAction";
import { getFilterState } from "../functions/filterState/filterState";
import { filterOverview } from "../utils/todo-utils";
import { Capacitor } from "@capacitor/core";
import { isHelpVisibleForPlatform } from "../utils/globalTasksHelp";
import { setCalendarSettings } from "../redux/actions/Calendar/setCalendarSettingsAction";
import { connectToSignalR, stopSignalRConnection, connectToCalendarSyncHub } from "../functions/syncConnection/syncConnection";
import { Toast } from "@everway/design-system-react";
import { getItem, setItem } from "../utils/storageHelper";
import { useIsMobile as useIsMobileHook } from "../hooks/useIsMobile";

const RootLayout = () => {
    const showHelp = isHelpVisibleForPlatform();
    const licenseCheck = useSelector(state => state.license.licenseValid);
    const licenseChecked = useSelector(state => state.license.licenseChecked);
    const licenseChecking = useSelector(state => state.license.licenseChecking);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showSlideoutMenu, setShowSlideoutMenu] = useState(false);
    const [showLicenseModal, setShowLicenseModal] = useState(false);
    const [showNotificationButtonModal, setShowNotificationButtonModal] = useState(false);
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [isLoadingUser, setIsLoadingUser] = useState(true);

    const feature = "Overview";
    const filterStateName = getFilterState(feature) ?? "overviewFilterState";
    const filterState = useSelector(state => state[filterStateName]);
    const showCompleted = filterState.CanSeeCompleted ?? false;
    const calendarMaxView = useSelector((state) => state.calendarMaxView);

    const closeLicenseModal = () => {
        if (showLicenseModal === true) {
            setShowLicenseModal(false);
        }
    };

    const closeNotificationButtonModal = () => {
        setShowNotificationButtonModal(false);
    };

    const [disableButtons, setDisableButtons] = useState(true);

    const dispatch = useDispatch();
    const setIsProjectDragging = (value) => {
        dispatch({ type: "SET_IS_DRAGGING", payload: value });
    };
    const setCurrentlyDraggedProjectId = (value) => {
        dispatch({ type: "SET_CURRENTLY_DRAGGED_PROJECT_ID", payload: value });
    };
    const projects = useSelector((state) => state.taskList.data);
    const location = useLocation();
    const isCalendarPage = location.pathname.includes('/Calendar');
    const isProjectDragging = useSelector((state) => state.isDragging.isDragging);
    const calendarMax = useSelector((state) => state.calendarMaxView);
    const zoomLevel = useSelector((state) => state.userSettings.zoomLevel);
    const closeSettingsModal = () => {
        setShowSettingsModal(false);
    };
    const openSettingsModal = () => {
        setShowSettingsModal(true);
    };
    const navigate = useNavigate();
    const defaultView = useSelector((state) => state.userSettings.defaultView);

    // Load firebase user on component mount
    useEffect(() => {
        const loadFirebaseUser = async () => {
            try {
                const userString = await getItem(`firebase:user:${import.meta.env.VITE_PUBLIC_APP_ENV}`);
                if (userString) {
                    const parsedUser = JSON.parse(userString);
                    setFirebaseUser(parsedUser);
                } else {
                    // No Firebase user in storage - try to reauthenticate
                    console.log('Firebase user not found - checking for valid OIDC session...');
                    const { attemptReauthentication } = await import('../utils/reauthenticate');
                    const reauthUser = await attemptReauthentication();
                    
                    if (reauthUser) {
                        console.log('✅ Reauthentication successful - user can stay on current page');
                        setFirebaseUser(reauthUser);
                    } else {
                        console.log('❌ Reauthentication failed - user will need to log in');
                    }
                }
            } catch (error) {
                console.error('Failed to load firebase user:', error);
            } finally {
                setIsLoadingUser(false);
            }
        };

        loadFirebaseUser();
    }, []);

    const handleDragStart = (start) => {
        onDragStartFunction(start, setIsProjectDragging,
            setCurrentlyDraggedProjectId);
    }

    const isMobile = useIsMobileHook();
    const isIOS = useIsIOS();

    const handleDragEnd = async (result) => {
        const task = findBy$IdInTreeList(projects, result.draggableId);
        await onDragEndFunction(task.id, projects, filterOverview(projects, filterState, new Date()), result, dispatch, showCompleted, setIsProjectDragging);
    }

    useEffect(() => {
        if (!licenseChecking && !licenseChecked && firebaseUser) {
            // IMPORTANT: Only run this after firebaseUser is available (after Login.js completes)
            // This prevents race conditions on first sign-in
            const checkLocalLicenseFirst = async () => {
                const license = await getItem("license");
                
                if (license === "true") {
                    // We have a valid license stored locally
                    // Skip backend check and mark as checked to avoid signout on server errors
                    console.log('[LICENSE] Using locally stored license - skipping backend validation');
                    await setLicense("true"); // Ensure it's saved
                    dispatch({ type: "FIREBASE_LICENSE_SUCCESS", payload: true });
                    dispatch({ type: "LICENSE_CHECKED", payload: true });
                } else if (license === "false") {
                    // Explicitly marked as false - check with backend
                    console.log('[LICENSE] Stored license is false - checking with backend');
                    dispatch({ type: "LICENSE_CHECKING", payload: true });
                    await dispatch(getFirebaseLicenseFile());
                } else {
                    // No license in storage yet - might be first sign-in
                    // Wait longer for Login.js to complete its license check (especially on native)
                    console.log('[LICENSE] No stored license - waiting for Login.js to complete');
                    const waitTime = Capacitor.isNativePlatform() ? 2000 : 1000;
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                    
                    // Recheck storage after waiting
                    const recheckLicense = await getItem("license");
                    if (recheckLicense === "true") {
                        console.log('[LICENSE] License found after wait');
                        dispatch({ type: "FIREBASE_LICENSE_SUCCESS", payload: true });
                        dispatch({ type: "LICENSE_CHECKED", payload: true });
                    } else if (recheckLicense === "false") {
                        console.log('[LICENSE] License is false after wait');
                        dispatch({ type: "FIREBASE_LICENSE_ERROR", payload: false });
                        dispatch({ type: "LICENSE_CHECKED", payload: true });
                    } else if (!licenseChecked) {
                        // Still no license, proceed with backend check
                        console.log('[LICENSE] Still no license - checking with backend');
                        dispatch({ type: "LICENSE_CHECKING", payload: true });
                        await dispatch(getFirebaseLicenseFile());
                    }
                }
            };
            
            checkLocalLicenseFirst();
        }
    }, [licenseChecking, licenseChecked, firebaseUser, dispatch]);

    useEffect(() => {
        const initializeSignalR = async () => {
            // Initialize SignalR for BOTH web and native platforms after authentication
            if (!firebaseUser) {
                return;
            }

            try {
                await connectToSignalR(dispatch);
            } catch (e) {
                console.error("SignalR Connection Failed:", e);
            }
        };

        initializeSignalR();

        // Cleanup: stop SignalR when component unmounts
        return () => {
            stopSignalRConnection();
        };
    }, [firebaseUser, dispatch]);

    useEffect(() => {
        const setupServiceWorker = async () => {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.addEventListener('message', async (event) => {
                    if (event.data && event.data.redirectUrl) {
                        await setItem("redirectUrl", event.data.redirectUrl);
                    }
                });
            }
        };

        setupServiceWorker();

        if (isMobile) {
            if (isIOS) {
                dispatch(setMobileProperties({
                    platform: "iOS", type: "mobile",
                    width: window.innerWidth,
                    height: window.innerHeight,
                    dpi: window.devicePixelRatio * 96
                }));
            } else {
                dispatch(setMobileProperties({
                    platform: "Android", type: "mobile",
                    width: window.innerWidth,
                    height: window.innerHeight,
                    dpi: window.devicePixelRatio * 96
                }));
            }
        } else {
            dispatch(setMobileProperties({
                platform: "Web", type: "web",
                width: window.innerWidth,
                height: window.innerHeight,
                dpi: window.devicePixelRatio * 96
            }));
        }
    }, []);

    useEffect(() => {
        const setup = async () => {
            const environment = await getItem("environment");

            if (!firebaseUser) {
                const currentPath = location.pathname.toLowerCase();
                const isAuthPage = currentPath.includes('/welcome') || 
                                  currentPath.includes('/login') || 
                                  currentPath.includes('/error') ||
                                  currentPath.includes('/calendarlogin');
                
                if (!isAuthPage) {
                    console.log(`Session expired or user not found. Redirecting to Welcome from: ${window.location.origin}`);
                    
                    try {
                        sessionStorage.clear();
                    } catch (error) {
                        console.error('Error clearing session storage:', error);
                    }
                    
                    navigate(`/Welcome`);
                }
            } else {
                if (environment !== import.meta.env.VITE_PUBLIC_APP_ENV) {
                    signOut();
                }

                const userDetails = {
                    name: firebaseUser.name,
                    email: firebaseUser.email,
                    photoUrl: firebaseUser.firebasePhotoUrl,
                };
                dispatch(setUserDetails(userDetails));

                const analyticsJson =
                {
                    name: "ApplicationStarted",
                    category: "Application",
                    feature: '',
                    customParams: ''
                };
                dispatch(sendEvent(analyticsJson));

                if (Capacitor.isNativePlatform()) {
                    askForNotificationPermission();
                }
                else if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) {
                    if (Notification.permission === 'default') {
                        if (isMobile && isIOS) {
                            setShowNotificationButtonModal(true);
                        } else {
                            await askForNotificationPermission();
                            await getSubscription();
                        }
                    } else if (Notification.permission === 'granted') {
                        await getSubscription();
                    }
                }

                if (location.pathname === '/') {
                    if (defaultView.toLowerCase() === 'calendar') {
                        if (useIsMobile()) {
                            navigate('/Overview');
                        } else {
                            navigate('/Calendar');
                        }
                    } else {
                        navigate(`/${defaultView}`);
                    }
                }
            }

            if (!isMobile) {
                setShowSlideoutMenu(true);
            }

            const settingsString = await getItem(`usersettings:${import.meta.env.VITE_PUBLIC_APP_ENV}`);
            if (settingsString) {
                try {
                    const settingsJson = JSON.parse(settingsString);
                    dispatch(setUserSettings(settingsJson));
                } catch (error) {
                    console.error('Failed to parse user settings:', error);
                }
            }

            const calendarSettingsString = await getItem(`userCalendarSettings:${import.meta.env.VITE_PUBLIC_APP_ENV}`);
            if (calendarSettingsString) {
                try {
                    const calendarSettingsJson = JSON.parse(calendarSettingsString);
                    dispatch(setCalendarSettings(calendarSettingsJson));

                    if (calendarSettingsJson?.colors) {
                        dispatch({ type: 'SET_CALENDAR_COLORS', payload: calendarSettingsJson.colors });
                    }
                } catch (error) {
                    console.error('Failed to parse calendar settings:', error);
                }
            }
        };

        if (!isLoadingUser) {
            setup();
        }
    }, [firebaseUser, isLoadingUser]);

    useEffect(() => {
        const checkLicense = async () => {
            // Don't show modal while license is still being checked OR hasn't been checked yet
            if (licenseChecking || !licenseChecked) {
                return;
            }

            // DEFENSIVE: If Redux state says license is valid, trust it over storage
            // This prevents race conditions where storage hasn't updated yet but Redux has
            if (licenseCheck === true) {
                return;
            }

            const license = await getItem("license");

            // Only show modal if BOTH Redux state AND storage confirm no valid license
            if (license !== null) {
                if (license !== "true") {
                    if (licenseCheck !== true) {
                        setShowLicenseModal(true);
                    }
                }
            } else if (licenseCheck !== true) {
                // Storage has no license value AND Redux says invalid
                // On first sign-in this might be a race condition, so add extra check
                await new Promise(resolve => setTimeout(resolve, 200));
                const recheckLicense = await getItem("license");
                
                if (recheckLicense !== "true" && licenseCheck !== true) {
                    setShowLicenseModal(true);
                }
            }
            setDisableButtons(true);
        };

        checkLicense();
    }, [licenseCheck, licenseChecking, licenseChecked]);

    
    if (isLoadingUser) {
        return (
            <div className="App">
                <LoadingScreen />
            </div>
        );
    }

    return (
        <div className="App">
            <EditModal />
            {firebaseUser ? (
                <div className="gt-container th-gt">
                    <Toast.Provider position="bottom-center" />
                    <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
                        {calendarMaxView && (
                            <NavComponent openSettingsModal={openSettingsModal} showHelp={showHelp} />
                        )}
                        <div className={`gt-content ${isCalendarPage ? 'gt-content--calendar' : ''}  ${!calendarMax ? 'gt-content--fullscreen' : ''}`}>
                            {location.pathname.toLowerCase().trim() === "/overview" && (
                                <UnsortedTasksPanel isProjectDragging={isProjectDragging} zoomLevel={zoomLevel} />
                            )}
                            {location.pathname.toLowerCase().trim() === "/due-dates" &&
                                (<>
                                    <SlideoutMenuButton toggleSlideoutMenu={setShowSlideoutMenu} />
                                    <SlideoutMenu showSlideoutMenu={showSlideoutMenu} toggleSlideoutMenu={setShowSlideoutMenu} />
                                </>)}
                            {location.pathname.toLowerCase().trim() !== "/calendar" && (
                                <PageControl />
                            )}

                            <Outlet />
                        </div>

                        <SettingsModal
                            onOpen={showSettingsModal}
                            onClose={closeSettingsModal}
                        />
                        <LicensingModal
                            onOpen={showLicenseModal}
                            onClose={closeLicenseModal}
                        />
                        <NotificationButtonModal
                            modalOpen={showNotificationButtonModal}
                            onClose={closeNotificationButtonModal}
                        />
                        <MobileNav />
                    </DragDropContext>
                </div>
            ) : (
                <LoadingScreen />
            )}
        </div>
    );
};

export default RootLayout;